import {
  layoutWithLines,
  measureNaturalWidth,
  prepareWithSegments,
} from "@chenglou/pretext";

const FEATURED_TITLE_WIDTH = 250;
const FEATURED_TITLE_MAX_HEIGHT = 350;
const FEATURED_CATEGORY_MIN_FONT_SIZE_REM = 1;
const REFERENCE_FONT_SIZE = 100;
const REFERENCE_FONT = `${REFERENCE_FONT_SIZE}px "rwe", sans-serif`;

/**
 * Finds the word boundary that produces the most evenly sized lines.
 */
function findBalancedTwoLineLayout(text) {
  const words = text.split(/\s+/);
  let bestLayout = null;
  let smallestDifference = Number.POSITIVE_INFINITY;

  for (let breakIndex = 1; breakIndex < words.length; breakIndex++) {
    const lineTexts = [
      words.slice(0, breakIndex).join(" "),
      words.slice(breakIndex).join(" "),
    ];
    const lines = lineTexts.map((lineText) => ({
      text: lineText,
      width: measureNaturalWidth(prepareWithSegments(lineText, REFERENCE_FONT)),
    }));
    const difference = Math.abs(Math.log(lines[0].width / lines[1].width));

    if (difference < smallestDifference) {
      bestLayout = lines;
      smallestDifference = difference;
    }
  }

  return bestLayout;
}

/**
 * Renders each line at the font size required to fill the featured title width.
 */
function renderFeaturedTitle(element) {
  const text = element.textContent.trim().toUpperCase();

  if (!text) {
    return;
  }

  const prepared = prepareWithSegments(text, REFERENCE_FONT);
  const wordCount = text.split(/\s+/).length;
  const lines =
    wordCount === 1
      ? layoutWithLines(prepared, Number.MAX_SAFE_INTEGER, REFERENCE_FONT_SIZE)
          .lines
      : findBalancedTwoLineLayout(text);

  if (!lines) {
    return;
  }

  const renderedLines = lines.map((line) => {
    const lineElement = document.createElement("span");
    const fontSize = (REFERENCE_FONT_SIZE * FEATURED_TITLE_WIDTH) / line.width;

    lineElement.textContent = line.text;
    lineElement.style.display = "block";
    lineElement.style.fontSize = `${fontSize}px`;
    lineElement.style.whiteSpace = "nowrap";

    return lineElement;
  });

  element.replaceChildren(...renderedLines);

  const elementHeight = element.getBoundingClientRect().height;

  if (elementHeight > FEATURED_TITLE_MAX_HEIGHT) {
    const styles = getComputedStyle(element);
    const fixedHeight = [
      styles.borderTopWidth,
      styles.borderBottomWidth,
      styles.paddingTop,
      styles.paddingBottom,
    ].reduce((height, value) => height + (parseFloat(value) || 0), 0);
    const scalableHeight = elementHeight - fixedHeight;
    const availableHeight = FEATURED_TITLE_MAX_HEIGHT - fixedHeight;
    const scale = availableHeight / scalableHeight;

    renderedLines.forEach((lineElement) => {
      const fontSize = parseFloat(lineElement.style.fontSize);
      lineElement.style.fontSize = `${fontSize * scale}px`;
    });
  }
}

/**
 * Keeps the featured category on one line within the featured title width.
 */
function renderFeaturedCategory(element) {
  const text = element.textContent.trim();

  if (!text) {
    return;
  }

  const styles = getComputedStyle(element);
  const fontSize = parseFloat(styles.fontSize);
  const font = `${styles.fontWeight} ${fontSize}px ${styles.fontFamily}`;
  const naturalWidth = measureNaturalWidth(prepareWithSegments(text, font));
  const rootFontSize = parseFloat(
    getComputedStyle(document.documentElement).fontSize,
  );
  const minFontSize = FEATURED_CATEGORY_MIN_FONT_SIZE_REM * rootFontSize;

  element.style.width = "fit-content";
  element.style.maxWidth = `${FEATURED_TITLE_WIDTH}px`;
  element.style.whiteSpace = "nowrap";

  if (naturalWidth > FEATURED_TITLE_WIDTH) {
    element.style.fontSize = `${Math.max(
      minFontSize,
      (fontSize * FEATURED_TITLE_WIDTH) / naturalWidth,
    )}px`;
  }
}

export function squareFonts(context) {
  once(
    "titan-rwe-title-page-layout",
    ".featured-article__title",
    context,
  ).forEach((el) => {
    const completePrintWait =
      window.titanPrint?.registerWait?.("titan-rwe-featured-title") ??
      (() => {});

    document.fonts.ready
      .then(() => {
        renderFeaturedTitle(el);

        const category = el
          .closest(".featured-article")
          ?.querySelector(".featured-article__category");

        if (category) {
          renderFeaturedCategory(category);
        }
      })
      .finally(completePrintWait);
  });
}
